import { motion, useInView } from "framer-motion";
import { useRef, useEffect, forwardRef } from "react";
const EASE = [0.16, 1, 0.3, 1];

export const MaskedLine = ({ children, delay = 0, play = true, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={play ? { y: "0%" } : { y: "115%" }}
      transition={{ duration: 1.2, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const FadeUp = ({ children, delay = 0, className = "", y = 40 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-12% 0px" }}
    transition={{ duration: 1.1, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const ChapterLabel = ({ num, name, mal, className = "" }) => (
  <FadeUp className={`flex items-baseline gap-4 ${className}`} y={24}>
    <span className="font-body text-[11px] tracking-[0.35em] text-brass">{num}</span>
    <span className="h-px w-10 bg-brass/50 self-center" />
    <span className="font-body text-[11px] tracking-[0.35em] text-cream/70 uppercase">{name}</span>
    {mal && <span className="font-mal text-sm text-cream/40">{mal}</span>}
  </FadeUp>
);

export const BackgroundVideo = forwardRef(({ src, poster, className, style, autoPlay = true, ...props }, ref) => {
  const localRef = useRef(null);

  const setRefs = (element) => {
    localRef.current = element;
    if (typeof ref === 'function') {
      ref(element);
    } else if (ref) {
      ref.current = element;
    }
  };

  const inView = useInView(localRef, { margin: "200px 0px" });

  useEffect(() => {
    const video = localRef.current;
    if (!video) return;

    if (inView && autoPlay) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Fallback for mobile autoplay restriction
        });
      }
    } else {
      video.pause();
    }
  }, [inView, autoPlay]);

  return (
    <video
      ref={setRefs}
      className={className}
      style={{ objectFit: "cover", transform: "scale(1.15)", transformOrigin: "center center", ...style }}
      src={src}
      poster={poster}
      muted
      loop
      playsInline
      webkit-playsinline="true"
      preload="metadata"
      {...props}
    />
  );
});

BackgroundVideo.displayName = "BackgroundVideo";
export const MotionBackgroundVideo = motion.create ? motion.create(BackgroundVideo) : motion(BackgroundVideo);
